<template>
  <div class="bulk-order-items-show">
    <PageNavigationBar v-if="!isItemSkuLoading" />
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <FeedbackError v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div class="items-show__body">
      <div :id="pageNavigationIds.GIFT_INFO" class="items-show__main-wrapper">
        <div v-if="isItemSkuLoading" class="item-sku-loading">
          <SmallLoading />
        </div>
        <div v-else class="items-show__main">
          <div class="items-show__image-area">
            <ImgLazy
              :image-url="itemSku.imageUrl"
              :alt-text="itemSku.itemName"
              aspect-ratio="1"
            />
            <LimitedTimeSalesInfo :ended-at="itemSku.limitedTimeSalesEndedAt" />
          </div>
          <div class="items-show__form-area">
            <div class="items-show__description-area">
              <div
                v-if="brand.listDisplayableItemPresent"
                class="description-area__brand-name"
              >
                <span>{{ itemSku.brandName }}</span>
              </div>
              <div v-else class="description-area__brand-name subhead">
                <span>{{ itemSku.brandName }}</span>
              </div>
              <div class="description-area__item-name title">
                <span>{{ itemSku.itemName }}</span>
              </div>
              <div class="description-area__item-sku-price-area">
                <div class="description-area__item-sku-price headline">
                  <span>{{ itemSkuPrice }}</span>
                </div>
                <span
                  v-if="itemSku.isIncludedTax"
                  class="description-area__tax-text"
                >
                  税込
                </span>
              </div>
              <div class="description-area__expired-date-area">
                <div class="description-area__expired-date-title caption">
                  <span>有効期限</span>
                </div>
                <div class="description-area__expired-date caption">
                  <span>{{ itemSku.expiredDate }}</span>
                </div>
              </div>
              <div class="description-area__usage-instruction-area">
                <div class="description-area__usage-instruction-title caption">
                  <span>ギフトの利用方法</span>
                </div>
                <div class="description-area__usage-instruction-link">
                  <span
                    class="description-area__usage-instruction-link-text"
                    @click="openUsageInstructionModal"
                  >
                    {{ itemSku.usageInstructionTitle }}
                  </span>
                </div>
              </div>
              <UsageInstructionModal
                v-if="isUsageInstructionModalActive"
                :usage-instruction-title="itemSku.usageInstructionTitle"
                :usage-instruction-description="
                  itemSku.usageInstructionDescription
                "
                @clicked-modal-cover="closeUsageInstructionModal"
                @clicked-close="closeUsageInstructionModal"
              />
            </div>
            <div class="items-show__ticket-count-area">
              <div
                v-if="hasSeveralItemSkus"
                class="ticket-count-area__item-sku-area"
              >
                <div class="ticket-count-area__selected-item-sku-name caption">
                  <span>選択中: {{ itemSku.name }}</span>
                </div>
                <div class="ticket-count-area__select-boxes--horizontal-scroll">
                  <HorizontalScroll
                    ref="select-box-horizontal"
                    ref-name="select-boxes"
                  >
                    <SelectBoxes
                      :items-name="'select_box'"
                      :items="selectBoxItemSkus"
                      :selected-value="itemSku.id"
                      :scrollable="false"
                      @input="selectRadioBtn"
                      @mount="onMountedSelectBoxes"
                    />
                  </HorizontalScroll>
                </div>
              </div>
              <div class="ticket-count-area__stepper-control-area">
                <div class="ticket-count-area__stepper-control-title subhead">
                  <span>枚数</span>
                </div>
                <div
                  class="ticket-count-area__stepper-control-box stepper-control__box"
                >
                  <span
                    :class="{
                      'stepper-control__btn--minus': !isMinusBtnActive,
                      'stepper-control__btn--minus--active': isMinusBtnActive
                    }"
                    @click="countDown"
                  />
                  <span class="stepper-control__number">
                    {{ itemSkuCount }}
                  </span>
                  <span
                    :class="{
                      'stepper-control__btn--plus': !isPlusBtnActive,
                      'stepper-control__btn--plus--active': isPlusBtnActive
                    }"
                    @click="countUp"
                  />
                </div>
              </div>
            </div>
            <div class="items-show__button-area">
              <div
                v-if="itemSku.isOutOfStock"
                class="error-message button-area__out-of-stock-message"
              >
                <span>選択中のギフトは入荷待ちです</span>
              </div>
              <button
                class="btn btn--primary button-area__send-gift-button"
                :disabled="disableAddGiftButton || null"
                @click="submitSendGiftButton"
              >
                このギフトを選択する
              </button>
            </div>
          </div>
        </div>
      </div>
      <GiftDetailArea
        :is-loading="isItemSkuLoading"
        :description-items="itemSku.descriptionItems"
        :mail-slot-deliverable="itemSku.mailSlotDeliverable"
        :description-html="itemSku.descriptionHtml"
        :staff-reviews="itemSku.staffReviews"
      />
      <SpecificationArea
        v-if="!isItemSkuLoading"
        :food-and-beverage-detail="itemSku.foodAndBeverageDetail"
        :set-contents="itemSku.setContents"
        :capacity-size-weight="itemSku.capacitySizeWeight"
        :other-specifications="itemSku.otherSpecifications"
      />
      <NoticeArea
        v-if="!isItemSkuLoading"
        :notice-detail="itemSku.noticeDetail"
      />
      <div class="items-show__brand-info-wrapper items-show__sub-wrapper">
        <div class="items-show__brand-info-content items-show__sub-content">
          <DecorationHead text="ブランド情報" :heading-level="2" />
          <div v-if="isBrandLoading" class="item-sku-loading">
            <SmallLoading />
          </div>
          <div v-else class="brand-info-content__body">
            <div class="brand-info-content__image-area">
              <ImgLazy
                :image-url="brand.imageUrl"
                :alt-text="brand.name"
                aspect-ratio="1"
                :width="80"
                :height="80"
                class="brand-info-content__image"
              />
              <div class="brand-info-content__brand-name body-bold">
                <span>{{ brand.name }}</span>
              </div>
            </div>
            <div class="brand-info-content__brand-description">
              <span>{{ brand.description }}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showableOtherItemsSection"
        class="items-show__other-items-wrapper items-show__sub-wrapper"
      >
        <div class="items-show__other-items-content items-show__sub-content">
          <DecorationHead text="このブランドの他のギフト" :heading-level="2" />
          <div v-if="isOtherItemsLoading" class="item-sku-loading">
            <SmallLoading />
          </div>
          <div v-else class="other-items-content__body">
            <div class="other-items-content__horizontal-scroll">
              <HorizontalScroll ref-name="other-items-content">
                <ItemBoxes :items="brandItems" :scrollable="false" />
              </HorizontalScroll>
            </div>
          </div>
        </div>
      </div>
      <div class="items-show__back-to-top">
        <span class="back-to-top-link subhead" @click="onClickBackToTop">
          ページトップへ ▲
        </span>
      </div>
      <SearchItemBottomLinks
        class="items-show__bottom-link"
        :back-to-gift-card-edit-url="backGiftCardEditUrl"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SelectBoxes from '../../../molecules/select_boxes/SelectBoxes.vue'
import SmallLoading from '../../../atoms/loadings/SmallLoading.vue'
import FeedbackError from '../../../atoms/feedbacks/feedback-error.vue'
import ItemBoxes from '../../../molecules/item_boxes/ItemBoxes.vue'
import HorizontalScroll from '../../../molecules/horizontal-scrolls/HorizontalScroll.vue'
import SearchItemBottomLinks from '../../../organism/bulk_personal_orders/bottom_links/SearchItemBottomLinks.vue'
import ImgLazy from '../../../atoms/images/ImgLazy.vue'
import UsageInstructionModal from '../../../organism/items/UsageInstructionModal.vue'
import { useBulkPersonalOrderSessionGiftCardStore } from '../../../../store/bulkPersonalOrderSessionGiftCard'
import { computed } from 'vue'
import { bulkOrderAgreementChecker } from '../../../../pages/bulk_order/common/bulkOrderAgreementChecker'
import LimitedTimeSalesInfo from '../../../molecules/items/LimitedTimeSalesInfo.vue'
import SpecificationArea from '../../../molecules/items/SpecificationArea.vue'
import NoticeArea from '../../../molecules/items/NoticeArea.vue'
import GiftDetailArea from '../../../molecules/items/GiftDetailArea.vue'
import DecorationHead from '../../../atoms/decoration/DecorationHead.vue'
import PageNavigationBar from '../../../molecules/items/PageNavigationBar.vue'
import { pageNavigationIds } from '../../../../constants/pageNavigationConstants'

export default {
  components: {
    SelectBoxes,
    SmallLoading,
    FeedbackError,
    ItemBoxes,
    SearchItemBottomLinks,
    HorizontalScroll,
    ImgLazy,
    UsageInstructionModal,
    LimitedTimeSalesInfo,
    SpecificationArea,
    NoticeArea,
    GiftDetailArea,
    DecorationHead,
    PageNavigationBar
  },
  props: {
    bulkPersonalOrderSessionId: {
      type: Number,
      required: true
    },
    giftCardSessionToken: {
      type: String,
      required: true
    },
    itemId: {
      type: String,
      required: true
    },
    brandId: {
      type: String,
      required: true
    },
    itemUrl: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const store = useBulkPersonalOrderSessionGiftCardStore()
    const { giftCardForms, addItemSkuContent } = store

    // サーバ側にギフトカードがある + ローカルストレージに編集中のデータがない場合は、ギフトカードリストに戻る
    // 直接URLを叩かない限り発生しないはず
    if (!giftCardForms[props.giftCardSessionToken]) {
      window.location.href = '/bulk_order/gift_cards'
    }

    const backGiftCardEditUrl = computed(() => {
      if (props.giftCardSessionToken === 'new') {
        return '/bulk_order/gift_cards/new'
      }
      return `/bulk_order/gift_cards/${props.giftCardSessionToken}/edit`
    })

    return { backGiftCardEditUrl, addItemSkuContent }
  },
  data() {
    return {
      pageNavigationIds,
      isItemSkuLoading: true,
      itemSkus: [],
      itemSku: '',
      selectedItemSkuId: 0,
      isMinusBtnActive: false,
      isPlusBtnActive: true,
      itemSkuCount: 1,
      showableErrorFeedback: false,
      errorMessage: '',
      isBrandLoading: true,
      isOtherItemsLoading: true,
      brand: '',
      brandItems: [],
      disableAddGiftButton: false,
      isUsageInstructionModalActive: false
    }
  },
  computed: {
    itemSkuPrice() {
      return `¥${this.itemSku.price.toLocaleString()}`
    },
    selectBoxItemSkus() {
      return this.itemSkus.map(itemSku => {
        return {
          name: itemSku.name,
          value: itemSku.id,
          is_out_of_stock: itemSku.isOutOfStock
        }
      })
    },
    selectedItemSkuIndex() {
      return this.selectBoxItemSkus.findIndex(v => v.value === this.itemSku.id)
    },
    hasSeveralItemSkus() {
      return this.itemSkus.length > 1
    },
    showableOtherItemsSection() {
      return this.brandItems.length >= 1
    },
    isEmptySelectedItemSkuId() {
      return this.selectedItemSkuId === 0
    },
    hasDescriptionItems() {
      return (
        this.itemSku.descriptionItems &&
        this.itemSku.descriptionItems.length > 0
      )
    },
    hasStaffReviews() {
      return this.itemSku.staffReviews && this.itemSku.staffReviews.length > 0
    }
  },
  beforeCreate() {
    // まとめてオーダーの注意事項に同意していない場合は、ギフトカードリストへ戻す
    bulkOrderAgreementChecker()
  },
  mounted() {
    this.getItemSkus()
    this.getBrand()
    this.getOtherItems()
  },
  methods: {
    async getItemSkus() {
      try {
        const res = await axios.get(
          `/api/v1/pickup/items/${this.itemId}/item_skus?selected_item_sku_id=${this.selectedItemSkuId}`
        )
        this.itemSkus = res.data.data
          .map(itemSku => itemSku.attributes)
          .map(sku => {
            return {
              id: sku.id,
              name: sku.name,
              imageUrl: sku.imageUrl,
              brandName: sku.brandName,
              price: sku.price,
              isIncludedTax: sku.isIncludedTax,
              hasMultipleItemSkus: sku.hasMultipleItemSkus,
              expiredDate: sku.expiredDate,
              usageInstructionTitle: sku.usageInstructionTitle,
              usageInstructionDescription: sku.usageInstructionDescription,
              isOutOfStock: sku.isOutOfStock,
              descriptionHtml: sku.descriptionHtml,
              specificationHtml: sku.specificationHtml,
              instructionHtml: sku.instructionHtml,
              itemName: sku.itemName,
              quantityLimit: 10, // まとめてオーダーの場合は銀行振込をしてもらうので、不正決済対策が不要。そのため10を固定
              limitedTimeSalesEndedAt: sku.limitedTimeSalesEndedAt,
              descriptionItems: sku.descriptionItems,
              mailSlotDeliverable: sku.mailSlotDeliverable,
              staffReviews: sku.staffReviews || [],
              setContents: sku.setContents || '',
              capacitySizeWeight: sku.capacitySizeWeight || '',
              otherSpecifications: sku.otherSpecifications || '',
              foodAndBeverageDetail: sku.foodAndBeverageDetail || null,
              noticeDetail: sku.noticeDetail || null
            }
          })
        this.itemSku =
          this.isEmptySelectedItemSkuId ||
          !this.itemSkus.some(v => v.id === this.selectedItemSkuId)
            ? this.itemSkus[0]
            : this.itemSkus.find(v => v.id === this.selectedItemSkuId)
        this.isPlusBtnActive =
          !this.itemSku.isOutOfStock && this.itemSku.quantityLimit !== 1
        this.disableAddGiftButton = this.itemSku.isOutOfStock
      } catch (error) {
        this.errorMessage =
          error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
      }
      this.isItemSkuLoading = false
    },
    countDown() {
      if (this.itemSku.isOutOfStock) return
      this.itemSkuCount = this.itemSkuCount - 1
      if (this.itemSkuCount <= 1) {
        this.isMinusBtnActive = false
        if (this.itemSku.quantityLimit > 1) {
          this.isPlusBtnActive = true
        }

        this.itemSkuCount = 1
        return
      }
      if (
        1 < this.itemSkuCount &&
        this.itemSkuCount <= this.itemSku.quantityLimit
      ) {
        this.isPlusBtnActive = true
        this.isMinusBtnActive = true
      }
    },
    countUp() {
      if (this.itemSku.isOutOfStock) return
      this.itemSkuCount = this.itemSkuCount + 1
      if (this.itemSkuCount >= this.itemSku.quantityLimit) {
        this.itemSkuCount = this.itemSku.quantityLimit
        this.isPlusBtnActive = false
        if (this.itemSku.quantityLimit > 1) {
          this.isMinusBtnActive = true
        }

        return
      }
      if (
        1 < this.itemSkuCount &&
        this.itemSkuCount < this.itemSku.quantityLimit
      ) {
        this.isPlusBtnActive = true
        this.isMinusBtnActive = true
      }
    },
    selectRadioBtn(value) {
      const itemSku = this.itemSkus.find(
        itemSku => itemSku.id === Number(value)
      )
      this.itemSku = itemSku
      this.itemSkuCount = 1
      this.isMinusBtnActive = false
      this.isPlusBtnActive =
        !itemSku.isOutOfStock && itemSku.quantityLimit !== 1
      this.disableAddGiftButton = this.itemSku.isOutOfStock
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    submitSendGiftButton() {
      this.disablePurchaseButtons()

      this.addGift()
    },
    addGift() {
      dataLayer.push({
        event: 'add_to_bulk_cart',
        bulk_personal_order_session_id: this.bulkPersonalOrderSessionId
      })

      const itemSkuContent = {
        itemId: this.itemId,
        itemName: this.itemSku.itemName,
        itemSkuId: this.itemSku.id,
        itemSkuName: this.itemSku.name,
        brandName: this.itemSku.brandName,
        expiredDate: this.itemSku.expiredDate,
        quantity: this.itemSkuCount,
        isMinusBtnActive: this.isMinusBtnActive,
        isPlusBtnActive: this.isPlusBtnActive,
        imageUrl: this.itemSku.imageUrl,
        isIncludingTax: this.itemSku.isIncludedTax,
        hasMultipleItemSkus: this.itemSku.hasMultipleItemSkus,
        price: this.itemSku.price,
        itemSkuQuantityLimit: this.itemSku.quantityLimit
      }

      this.addItemSkuContent(this.giftCardSessionToken, itemSkuContent)
      window.location.href = this.backGiftCardEditUrl
    },
    disablePurchaseButtons() {
      this.disableAddGiftButton = true
    },
    enablePurchaseButtons() {
      this.disableAddGiftButton = false
    },
    openUsageInstructionModal() {
      this.isUsageInstructionModalActive = true
    },
    closeUsageInstructionModal() {
      this.isUsageInstructionModalActive = false
    },
    async getBrand() {
      try {
        const res = await axios.get(`/api/v1/pickup/brands/${this.brandId}`)
        this.brand = {
          name: res.data.data.attributes.name,
          imageUrl: res.data.data.attributes.icon_image_url,
          description: res.data.data.attributes.description,
          listDisplayableItemPresent:
            res.data.data.attributes.list_displayable_item_present
        }
      } catch (error) {
        this.errorMessage =
          error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
      }
      this.isBrandLoading = false
    },
    async getOtherItems() {
      try {
        const res = await axios.get(
          `/api/v1/pickup/brands/${this.brandId}/items`,
          {
            params: {
              excluded_item_id: this.itemId,
              limit: 30
            }
          }
        )
        this.brandItems = res.data.data
          .map(item => item.attributes)
          .map(item => {
            return {
              id: item.id,
              linkUrl: `/bulk_order/items/${item.id}?token=${this.giftCardSessionToken}`,
              imageUrl: item.item_box_image_url,
              brandName: item.brand_name,
              itemName: item.name,
              itemPrice: `¥${item.min_price.toLocaleString()}`,
              isIncludingTax: item.is_including_tax,
              hasMultipleItemSkus: item.item_sku_count > 1,
              soldOut: item.sold_out,
              labels: (item.labels || []).filter(label => label !== 'campaign')
            }
          })
      } catch (error) {
        this.errorMessage =
          error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
      }
      this.isOtherItemsLoading = false
    },
    onMountedSelectBoxes() {
      const horizontal = this.$refs['select-box-horizontal']
      horizontal.initializeValues()
      // NOTE: 横スクロール量 = (横スクロール部分の全長 / itemSkuの数) * 選択されたitemSkuのindex
      horizontal.scrollTo(
        (horizontal.scrollBoxScrollWidth / this.selectBoxItemSkus.length) *
          this.selectedItemSkuIndex
      )
    },
    onClickBackToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }
}
</script>
<style lang="scss" scoped>
.bulk-order-items-show {
  background-color: $gray-3;

  .items-show {
    &__main-wrapper {
      background-color: $white;
      padding-top: 48px;
      padding-bottom: 32px;

      @media screen and (max-width: $base-contents-width) {
        padding-top: 0;
        overflow: hidden; // item-sku-loadingのmarginが親要素を突き抜けないようにする
      }

      .item-sku-loading {
        margin: 40px auto;
        width: 24px;
        height: 24px;
      }
    }

    &__main {
      display: flex;
      justify-content: space-between;
      max-width: $base-contents-width;
      margin: 0 auto;
      padding: 0 24px;
    }

    &__image-area {
      text-align: left;
      display: flex;
      flex-direction: column;
      padding-right: 16px;

      // 768px * 0.4 = 307.2px
      width: 307px;
      height: 307px;
    }

    &__form-area {
      text-align: left;
      // 768px * 0.5 = 384px
      width: 384px;
    }

    &__description-area {
      .description-area {
        &__brand-name {
          margin-bottom: 8px;
          font-size: 1.4rem;
        }

        &__item-name {
          margin-bottom: 16px;
          font-weight: bold;
        }

        &__item-sku-price-area {
          display: flex;
          align-items: baseline;
        }

        &__item-sku-price {
          margin: 0 4px 16px 0;
          font-weight: bold;
        }

        &__tax-text {
          font-size: 1.2rem;
        }

        &__expired-date-area {
          margin-bottom: 8px;
          display: flex;
        }

        &__expired-date-title {
          padding-left: 20px;
          margin-right: 4px;
          @include bg-settings(
            '../../../../../../images/icons/clock-outline.svg',
            $white,
            16px,
            18px
          );
          text-align: left;
          color: $black;
        }

        &__expired-date {
          color: $black;
        }

        &__usage-instruction-area {
          margin-bottom: 24px;
        }

        &__usage-instruction-title {
          padding-left: 20px;
          margin-right: 4px;
          @include bg-settings(
            '../../../../../../images/icons/gift-outline.svg',
            $white,
            16px,
            18px
          );
          text-align: left;
          color: $black;
        }

        &__usage-instruction-link {
          margin-top: 4px;
          padding-left: 20px;
        }

        &__usage-instruction-link-text {
          @media (hover: hover) {
            &:hover {
              opacity: 0.7;
            }
          }
          font-size: 1.2rem;
          color: $link;
          cursor: pointer;
          line-height: 1.6;
        }

        &__disabled-payment-method {
          margin-bottom: 16px;
        }
      }
    }

    &__ticket-count-area {
      margin: 0 auto 32px;

      .ticket-count-area {
        &__item-sku-area {
          // 32px - 4px(select boxesのpadding分)
          margin-bottom: 28px;
        }

        &__selected-item-sku-name {
          padding: 16px 0;
          color: $black;
        }

        &__stepper-control-area {
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        &__stepper-control-title {
          margin-right: 16px;
        }
      }
    }

    &__button-area {
      margin: 0 auto;
      text-align: center;

      .button-area {
        &__out-of-stock-message {
          margin-bottom: 16px;
          text-align: center;
        }

        &__send-gift-button {
          margin-bottom: 16px;
        }
      }
    }

    &__sub-wrapper {
      margin: 16px auto 0 auto;
      background-color: $white;
      text-align: left;

      .item-sku-loading {
        margin: 40px auto;
        width: 24px;
        height: 24px;
      }
    }

    &__sub-content {
      margin: 0 auto;
      padding-bottom: 24px;
      max-width: $base-contents-width;

      .box-list--alert,
      .box-list--attention {
        // 720px - 8px * 2
        max-width: 704px;
      }
    }

    &__description-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    &__sub-content-body {
      margin: auto;
      max-width: 720px;
      width: 100%;
      width: -moz-available;
      width: -webkit-fill-available;
      width: stretch;

      @media all and (max-width: ($base-contents-width - 1)) {
        padding: 0 16px;
      }

      // KittyHtmlSanitizedの中の要素を全て適用したいため、:deepをつけている
      :deep(*) {
        box-sizing: border-box;
      }
    }

    &__brand-info-content {
      .brand-info-content {
        &__body {
          width: auto;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 24px;

          @media all and (max-width: ($base-contents-width - 1)) {
            padding: 24px 16px;
          }
        }

        &__image-area {
          display: flex;
          align-items: center;
        }

        &__image {
          border-radius: 8px;
          border: 1px solid $gray-2;
        }

        &__brand-name {
          flex: 1;
          margin-left: 16px;
        }

        &__brand-description {
          @extend %caption;
          color: $black;

          span {
            white-space: pre-wrap;
          }
        }
      }
    }

    &__other-items-content {
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        padding: 0;
      }

      .other-items-content {
        &__body {
          padding: 24px 16px 0 16px;
          overflow: hidden;

          @media all and (max-width: ($base-contents-width - 1)) {
            padding-right: 0;
          }
        }

        &__horizontal-scroll {
          width: 100%;
        }
      }
    }

    &__back-to-top {
      margin-top: 16px;
      padding: 16px;
      text-align: center;
      background-color: $gray-2;

      .back-to-top-link {
        @media (hover: hover) {
          &:hover {
            opacity: 0.7;
          }
        }
        box-sizing: border-box;
        display: inline;
        color: $link;
        cursor: pointer;
      }
    }

    &__bottom-link {
      padding: 16px 0;
    }

    // 画面の横幅が 768px (iPad mini) 未満の場合
    @media screen and (max-width: ($base-contents-width - 1)) {
      &__sub-content {
        max-width: $small-contents-width;
      }

      &__main {
        margin-top: 0;
        padding: 0;
        display: block;
        max-width: $small-contents-width;
      }

      &__image-area {
        padding: 0;
        width: $small-contents-width;
        height: $small-contents-width;
      }

      &__form-area {
        margin-top: 16px;
        margin-bottom: 0;
        padding: 0 16px;
        width: 100%;
        box-sizing: border-box;
      }
    }
  }
}
</style>
